/* eslint-disable no-nested-ternary */
/* eslint-disable react/jsx-no-duplicate-props */
import { isEmpty } from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { useForm, Controller } from 'react-hook-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import {
    Box,
    Button,
    Typography,
    Popper,
    Grow,
    Paper,
    MenuItem,
    TextField,
    Zoom,
    Tooltip,
    FormControl,
    Select,
    Avatar,
    Collapse,
    Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import ClickAwayListener from '@mui/material/ClickAwayListener';
// constants
import { getIconAndColor } from 'src/utils/taskPriority';
import { grey } from 'src/theme/palette';
// components
import { Iconify } from '..';
import { CustomAvatar, AssignUserAvatar } from '../custom-avatar';
import { NormalLoading } from '../loading';
import { AssignTaskPopup } from '../dialogPopUp';
import LeadActionZone from './LeadActionZone';
// store
import { useSelector, dispatch } from '../../redux/store';
import { getActivities } from '../../redux/slices/activity';
import {
    updateReminder,
    updateLeadTaskCounter,
    getLeadReminderData,
    followUpSlice,
    getReminderData,
    addNotes,
} from '../../redux/slices/lead';
import { clearPracticeUserData } from '../../redux/slices/practiceUser';
// hooks
import { FormProvider } from '../hook-form';
import { useAssignTaskToggle, useBoolean, useAuth } from '../../hooks';
// utils
import capitalizeFirstLetter from '../../utils/capitalizedFunc';
import { isDentallySyncTask } from '../../utils/getTasksData';
import { getAssignees } from '../../utils/multiAsignees';
// style
import {
    assignTaskHandlerStyle,
    assignUserAvatarStyle,
    assignLengthStyle,
} from '../../styles/leadTaskReminder';
import { AISummaryModal } from '../callScriptV2/components';

// Primary accent — matches reference design
const ACCENT = '#00A76F';

// Journey stages
const JOURNEY_STAGES = [
    { value: 'NEW', label: 'New', color: '#3b82f6' },
    { value: 'CONSULTATION_BOOKED', label: 'Consultation Booked', color: '#f59e0b' },
    { value: 'CONSULTATION_ATTENDED', label: 'Consultation Attended', color: '#8b5cf6' },
    { value: 'TREATMENT_STARTED', label: 'Treatment Started', color: '#00A76F' },
    { value: 'CLOSED', label: 'Completed', color: '#10b981' },
];

// Helper function to check if a note is an AI note
const parseAINote = (noteContent) => {
    if (!noteContent) return null;
    try {
        const parsed = JSON.parse(noteContent);
        if (parsed && parsed.AI_NOTES === true) {
            return parsed;
        }
    } catch (e) {
        // Not a JSON note
    }
    return null;
};

const getJourneyIndex = (status) => {
    const map = {
        NEW: 0, TODO: 0, CALLBACK: 0,
        CONSULTATION_BOOKED: 1, FAILED_TO_ATTEND_CONSULTATION: 1,
        CONSULTATION_ATTENDED: 2, TREATMENT_STARTED: 3, CLOSED: 4,
    };
    return map[status] ?? -1;
};

const PRIORITY_CONFIG = {
    URGENT: { label: 'Urgent', color: '#dc2626', bg: '#fef2f2' },
    HIGH: { label: 'High', color: '#f59e0b', bg: '#fffbeb' },
    NORMAL: { label: 'Normal', color: '#6b7280', bg: '#f3f4f6' },
    LOW: { label: 'Low', color: '#3b82f6', bg: '#eff6ff' },
};

// ------------------------------------------------------------------

const TaskSidebar = ({ data, getReminderBtn, handleFollowUp, handleSendWhatsApp, type, overViewbtns, setReloadCounters, onClose, parentType, onTaskUpdate }) => {
    const navigate = useNavigate();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const resolvedLeadId = (() => {
        if (!data) return null;
        const lead = data.lead;
        if (lead && typeof lead === 'object' && lead.id) return lead.id;
        if (typeof lead === 'number') return lead;
        if (typeof lead === 'string' && /^\d+$/.test(lead)) return Number(lead);
        return null;
    })();
    const { reminderPriority, reminderMethod } = useSelector((state) => state.schema);
    const { enqueueSnackbar } = useSnackbar();
    const [priorityFilter, setPriorityFilter] = useState('');
    const {
        taskDetail,
        isTasksCompleted,
        dashboardTaskPageFilter,
        leadNotes,
        taskTableData: { tasks: { limit, offset, filter, assignUser } }
    } = useSelector((state) => state.lead);
    const [dueDatetimeValue, setDueDatetimeValue] = useState(moment(data?.datetime, 'DD/MM/YYYY hh:mm:ss').format('ddd[,] D MMMM [at] hh:mm A'));
    const [detailValue, setDetailValue] = useState(data?.note || '');
    const [comment, setComment] = useState('');
    const [markButton, setMarkButton] = useState(data?.is_completed || false);
    const [anchorElAssign, setAnchorElAssign] = useState(null);
    const [commentHide, setCommentHide] = useState(true);
    const [detailsExpanded, setDetailsExpanded] = useState(true);
    const { user } = useAuth();

    // AI Summary Modal
    const [aiSummaryModalOpen, setAISummaryModalOpen] = useState(false);
    const [selectedAIData, setSelectedAIData] = useState(null);
    const handleOpenAISummary = (aiData) => { setSelectedAIData(aiData); setAISummaryModalOpen(true); };
    const handleCloseAISummary = () => { setAISummaryModalOpen(false); setSelectedAIData(null); };

    useEffect(() => {
        if (!isEmpty(taskDetail?.priority)) {
            setPriorityFilter(taskDetail?.priority);
        }
    }, [taskDetail?.priority]);

    useEffect(() => {
        if (data) {
            setValue('relation', data?.lead_treatment?.id);
            setValue('actionType', data?.method);
            setDetailValue(data?.note || '');
            setMarkButton(data?.is_completed || false);
            if (data?.datetime) {
                setValue('DueDatetime', moment(data?.datetime, 'DD/MM/YYYY hh:mm:ss').format('DD/MM/YYYY HH:mm'));
            }
            if (setReloadCounters) setReloadCounters(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const openCalendar = useBoolean();
    const defaultValues = { relation: data?.lead_treatment?.id || '', actionType: data?.method || '' };
    const methods = useForm({ mode: 'onChange', defaultValues });
    const { setValue, getValues, control } = methods;

    // --- handlers ---
    const dueDateChange = (value) => setDueDatetimeValue(value);
    const dueDateBlur = (value) => {
        const reminderData = { datetime: value ? `${moment(value).format('DD-MM-YYYY HH:mm:ss')}.0Z` : null, assignees: data?.assignees?.map((el) => el?.id) };
        if (value) { openCalendar.onFalse(); dispatch(updateReminder(reminderData, data?.id, practiceId, handleReminder, 'markComplete', handleSuccess)); }
    };
    const handleActionTypeChange = (e) => {
        const reminderData = { method: e?.target?.value || '', assignees: data?.assignees?.map((el) => el?.id) };
        if (e?.target?.value) dispatch(updateReminder(reminderData, data?.id, practiceId, handleReminder, 'markComplete', handleSuccess));
    };
    // Reload data without closing the sidebar
    const reloadData = () => {
        setComment('');
        dispatch(updateLeadTaskCounter([]));
        dispatch(getReminderData(practiceId, data?.id));

        if (parentType === 'LeadDetail') {
            dispatch(followUpSlice(data?.lead?.id, practiceId, 'LeadDetail'));
            dispatch(getActivities(practiceId, data?.lead?.id, 10, 0));
            if (onTaskUpdate) onTaskUpdate();
        } else if (parentType !== 'Tasks') {
            // Dashboard: skip getLeadReminderData here —
            // the reloadCounters effect in TaskReminder handles the task list refetch
            // with the correct current filter params (assignees, date range, treatment, etc.)
            // Calling it here too causes a duplicate fetch with stale closure values.
            dispatch(followUpSlice(data?.lead?.id, practiceId, 'LeadDetail'));
            dispatch(getActivities(practiceId, data?.lead?.id, 10, 0));
        }
    };
    const handleReminder = () => {
        reloadData();
        if (setReloadCounters) { setReloadCounters(true); onClose(); }
    };
    const handleSuccess = (d) => {
        enqueueSnackbar(d.message || '', { variant: d.success ? 'success' : 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
    };
    const { toggle: openAssignTask, onOpen: onOpenAssignTask, onClose: onCloseAssignTask } = useAssignTaskToggle();
    const assignTaskHandler = (event) => { setAnchorElAssign(anchorElAssign ? null : event.currentTarget); onOpenAssignTask(); };
    const allPopupCloseHandler = (value) => {
        if (value === 'Assigned') { onCloseAssignTask(); setAnchorElAssign(null); }
        dispatch(getReminderData(practiceId, data?.id));
        dispatch(getLeadReminderData(practiceId, 'Tasks', limit, offset, filter, '', assignUser, '', '', '', dashboardTaskPageFilter, isTasksCompleted));
    };
    const handlePriorityFilterChange = (e) => {
        setPriorityFilter(e.target.value);
        dispatch(updateReminder({ priority: e.target.value || '' }, data?.id, practiceId, handleReminder, 'markComplete', handleSuccess));
    };
    const onBlurEvent = (value) => {
        dispatch(updateReminder({ note: value || '', assignees: data?.assignees?.map((el) => el?.id) }, data?.id, practiceId, handleReminder, 'markComplete', handleSuccess));
    };
    const onBlurCommentEvent = () => {
        dispatch(addNotes({ lead: data?.lead?.id, additional_notes: comment || '' }, practiceId, handleReminder, 'leadNotePopup'));
    };
    const markButtonHandler = () => {
        dispatch(updateReminder({ is_completed: !markButton }, data?.id, practiceId, handleReminder, 'markComplete', handleSuccess));
    };
    const onClickAwayEvent = () => {
        if (document.querySelector('.MuiModal-root')) return;
        onCloseAssignTask(); setAnchorElAssign(null); dispatch(clearPracticeUserData());
    };

    // --- callbacks for LeadActionZone ---
    const handleSidebarSuccess = (actionType) => {
        if (actionType === 'BOOK_CLOSE' || actionType === 'CLOSE' || actionType === 'ARCHIVE') {
            if (setReloadCounters) setReloadCounters(true);
            if (onClose) onClose();
        } else if (actionType === 'FOLLOW_UP') {
            if (type === 'Task-overView') overViewbtns(data?.lead_treatment?.status, data?.lead_treatment, data?.lead?.is_archived);
            // #320b — fall back to data?.lead?.id when lead_treatment.lead
            // is null (happens on V2-mirrored reminders where the V1 lead FK
            // isn't populated). Without the fallback the downstream
            // handleFollowUp `effectiveLeadId` guard silently no-ops and the
            // popup never opens. data?.lead?.id is the canonical reminder.lead
            // object id, present for every reminder regardless of origin.
            else if (handleFollowUp) handleFollowUp(data?.lead_treatment?.status, data?.lead_treatment?.lead || data?.lead?.id, data.id, data?.lead_treatment, data?.lead?.is_archived, data);
            else getReminderBtn(data?.lead_treatment?.status, data?.lead_treatment?.lead, data.id, data?.lead_treatment, data?.lead?.is_archived, getValues('actionType'));
        } else if (actionType === 'SEND_WHATSAPP') {
            if (handleSendWhatsApp) handleSendWhatsApp(data);
        } else if (actionType === 'MARK_COMPLETE_MANUAL_WS') {
            dispatch(updateReminder({ is_completed: true }, data?.id, practiceId, handleReminder, 'markComplete', handleSuccess));
        } else {
            reloadData();
        }
    };

    // --- computed ---
    const assigneeData = data?.assignees?.filter((el) => el?.user?.first_name && !el?.user?.is_superuser);
    const isOverdue = data.datetime && moment(data?.datetime, 'DD/MM/YYYY hh:mm:ss').isBefore(moment()) && !data.is_completed;
    const journeyIdx = getJourneyIndex(data?.lead_treatment?.status || data?.group);
    const taskTitle = isDentallySyncTask(data?.note)
        ? 'Sync with Dentally'
        : data.title !== ''
            ? capitalizeFirstLetter(data?.title)
            : `${capitalizeFirstLetter(data?.method === 'OTHER' ? 'Discuss with' : data?.method || '')} ${data?.lead?.first_name || ''} ${data?.lead?.last_name || ''} about ${data?.lead_treatment?.treatment || ''}`;
    const scheduledDate = data?.datetime ? moment(data?.datetime, 'DD/MM/YYYY hh:mm:ss').format('ddd, D MMMM [at] hh:mm A') : '';
    const patientName = `${capitalizeFirstLetter(data?.lead?.first_name || '')} ${capitalizeFirstLetter(data?.lead?.last_name || '')}`;
    const patientInitials = `${(data?.lead?.first_name || '')[0] || ''}${(data?.lead?.last_name || '')[0] || ''}`.toUpperCase();

    return (
        <FormProvider methods={methods} formStyle={{ height: '100%' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <NormalLoading pageCount={(Object.values(data)?.length <= 0 && 'No Found')} tableData={(Object.values(data)?.length <= 0 && 'No Found')} />

                {/* ─── SCROLLABLE CONTENT ─── */}
                <Box sx={{ flex: 1, overflow: 'auto' }}>

                    {/* ── TASK HEADER ── */}
                    <Box sx={{ p: '20px 20px 16px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                            <Tooltip title={markButton ? 'Mark as incomplete' : 'Mark as complete'} placement="top">
                                <Box
                                    onClick={() => markButtonHandler()}
                                    sx={{
                                        width: 24, height: 24, minWidth: 24, mt: '2px',
                                        borderRadius: '50%', cursor: 'pointer',
                                        border: markButton ? 'none' : '2px solid #d0d0d0',
                                        bgcolor: markButton ? ACCENT : 'transparent',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    {markButton && <Iconify icon="mdi:check" width={16} sx={{ color: '#fff' }} />}
                                </Box>
                            </Tooltip>
                            <Box>
                                <Typography sx={{
                                    fontSize: '16px', fontWeight: 600, color: 'text.primary', lineHeight: 1.4,
                                    textDecoration: markButton ? 'line-through' : 'none',
                                    opacity: markButton ? 0.5 : 1,
                                }}>
                                    {taskTitle}
                                </Typography>
                                {scheduledDate && (
                                    <Typography sx={{ fontSize: '13px', color: 'text.disabled', mt: 0.5 }}>
                                        {scheduledDate}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* ── OVERDUE WARNING ── */}
                    {isOverdue && (
                        <Box sx={{
                            mx: 2.5, mb: 2, px: 1.75, py: 1.25,
                            bgcolor: '#fef2f2', borderRadius: '10px',
                            borderLeft: '3px solid #dc2626',
                            display: 'flex', alignItems: 'center', gap: 1,
                        }}>
                            <Iconify icon="mdi:alert" width={14} sx={{ color: '#dc2626' }} />
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#dc2626' }}>
                                {moment(data?.datetime, 'DD/MM/YYYY hh:mm:ss').fromNow(true)} overdue
                            </Typography>
                        </Box>
                    )}

                    {/* ── COMPACT CONTEXT STRIP ── */}
                    <Box sx={{ px: 2.5, pb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box sx={{
                            width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                            background: `linear-gradient(135deg, ${ACCENT} 0%, #00C897 100%)`,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '12px', fontWeight: 600, color: '#fff',
                        }}>
                            {patientInitials}
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#1a1a1a', lineHeight: 1.3 }}>
                                {patientName}
                            </Typography>
                            <Typography sx={{ fontSize: '12px', color: '#888', lineHeight: 1.3 }} noWrap>
                                {data?.lead_treatment?.treatment || '-'}
                            </Typography>
                        </Box>
                        {journeyIdx >= 0 && (
                            <Chip
                                label={JOURNEY_STAGES[journeyIdx]?.label}
                                size="small"
                                sx={{
                                    fontSize: '11px', fontWeight: 600, height: 24,
                                    bgcolor: alpha(JOURNEY_STAGES[journeyIdx]?.color, 0.12),
                                    color: JOURNEY_STAGES[journeyIdx]?.color,
                                    borderRadius: '6px',
                                }}
                            />
                        )}
                        {resolvedLeadId && practiceId && parentType !== 'LeadDetail' && (
                            <Tooltip title="Go to lead detail" placement="top" arrow>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => { onClose?.(); navigate(`/dashboard/practice/${practiceId}/lead-detail/${resolvedLeadId}`); }}
                                    sx={{
                                        minWidth: 0, p: '4px', flexShrink: 0,
                                        color: 'text.disabled',
                                        '&:hover': { color: ACCENT, bgcolor: alpha(ACCENT, 0.08) },
                                    }}
                                >
                                    <Iconify icon="eva:external-link-fill" width={16} />
                                </Button>
                            </Tooltip>
                        )}
                    </Box>

                    {/* ── ACTION ZONE ── */}
                    <LeadActionZone
                        lead={data?.lead}
                        leadTreatment={data?.lead_treatment}
                        practiceId={practiceId}
                        onSuccess={handleSidebarSuccess}
                        onActionComplete={markButtonHandler}
                        taskMethod={data?.method}
                    />


                    {/* ── PATIENT JOURNEY PROGRESS ── */}
                    <Box sx={{ px: 2.5, pb: 2.5 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            {JOURNEY_STAGES.map((stage, i) => {
                                const isActive = i <= journeyIdx;
                                const isCurrent = i === journeyIdx;
                                return (
                                    <Fragment key={stage.value}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5 }}>
                                            <Box sx={{
                                                width: isActive ? 8 : 6, height: isActive ? 8 : 6,
                                                borderRadius: '50%', bgcolor: isActive ? stage.color : '#e0e0e0',
                                                transition: 'all 0.2s ease', flexShrink: 0,
                                            }} />
                                            {isCurrent && (
                                                <Typography sx={{ fontSize: '11px', fontWeight: 500, color: stage.color, whiteSpace: 'nowrap' }}>
                                                    {stage.label}
                                                </Typography>
                                            )}
                                        </Box>
                                        {i < JOURNEY_STAGES.length - 1 && (
                                            <Box sx={{ flex: 1, height: 2, borderRadius: '1px', bgcolor: i < journeyIdx ? JOURNEY_STAGES[i + 1].color : '#e8e8e8', minWidth: 6 }} />
                                        )}
                                    </Fragment>
                                );
                            })}
                        </Box>
                    </Box>

                    {/* ── DETAILS ACCORDION (user-controlled) ── */}
                    <Box sx={{ px: 2.5 }}>
                        <Box
                            onClick={() => setDetailsExpanded((prev) => !prev)}
                            sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 1, py: 1.5 }}
                        >
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#555' }}>Details</Typography>
                            <Iconify icon="mdi:chevron-down" width={16} sx={{ color: '#888', transform: detailsExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s ease' }} />
                        </Box>
                        <Collapse in={detailsExpanded} timeout={200}>

                        {/* Patient */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
                            <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', color: '#aaa' }}>
                                <Iconify icon="lets-icons:user" width={15} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '11px', color: '#999', mb: 0.25 }}>Patient</Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: ACCENT, cursor: 'pointer' }}>
                                    {patientName}
                                </Typography>
                            </Box>
                            <Box sx={{
                                width: 32, height: 32, borderRadius: '50%',
                                background: `linear-gradient(135deg, ${ACCENT} 0%, #00C897 100%)`,
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '11px', fontWeight: 600, color: '#fff',
                            }}>
                                {patientInitials}
                            </Box>
                        </Box>

                        {/* Action */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
                            <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', color: '#aaa' }}>
                                <Iconify icon="mdi:flash-outline" width={15} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '11px', color: '#999', mb: 0.25 }}>Trigger action</Typography>
                                <FormControl variant="standard" size="small" sx={{ minWidth: 100 }}>
                                    <Controller
                                        name="actionType"
                                        control={control}
                                        render={({ field }) => (
                                            <Select
                                                {...field}
                                                variant="standard"
                                                defaultValue={defaultValues?.actionType}
                                                sx={{
                                                    fontSize: '14px', fontWeight: 500, color: ACCENT,
                                                    '&:before': { borderBottom: 'none' },
                                                    '&:after': { borderBottom: `2px solid ${ACCENT}` },
                                                    '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                                                    '.MuiSelect-select': { py: 0, pr: '20px !important' },
                                                }}
                                                onChange={(e) => { field.onChange(e); handleActionTypeChange(e); }}
                                            >
                                                {reminderMethod?.map((reminder) => (
                                                    <MenuItem key={reminder.value} value={reminder.value}>
                                                        <Typography sx={{ fontSize: '13px' }}>{reminder.label}</Typography>
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        )}
                                    />
                                </FormControl>
                            </Box>
                        </Box>

                        {/* Treatment */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
                            <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', color: '#aaa' }}>
                                <Iconify icon="mdi:heart-outline" width={15} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '11px', color: '#999', mb: 0.25 }}>Treatment</Typography>
                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: ACCENT }}>
                                    {data?.lead_treatment?.treatment || '-'}
                                </Typography>
                            </Box>
                        </Box>

                        {/* Due Date */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
                            <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', color: '#aaa' }}>
                                <Iconify icon="mdi:calendar-outline" width={15} />
                            </Box>
                            <Box sx={{ flex: 1, position: 'relative' }}>
                                <Typography sx={{ fontSize: '11px', color: '#999', mb: 0.25 }}>Due date</Typography>
                                <Button
                                    onClick={() => openCalendar.onTrue()}
                                    sx={{
                                        p: 0, minWidth: 0, textTransform: 'none',
                                        fontSize: '14px', fontWeight: 500,
                                        color: isOverdue ? '#dc2626' : '#1a1a1a',
                                        '&:hover': { background: 'none', textDecoration: 'underline' },
                                    }}
                                    disableRipple
                                >
                                    {scheduledDate || 'Set date'}
                                </Button>
                                <Box sx={{ position: 'absolute', top: 0, left: 0, width: 0, height: 0, overflow: 'hidden', opacity: 0 }}>
                                    <DateTimePicker
                                        orientation="landscape" ampm={false} label=""
                                        open={openCalendar.value} onClose={openCalendar.onFalse}
                                        name="DueDatetime" value={dueDatetimeValue}
                                        minDateTime={new Date()} inputFormat="dd/MM/yyyy HH:mm"
                                        onChange={(v) => dueDateChange(v)} onAccept={(v) => dueDateBlur(v)}
                                        slotProps={{ textField: { fullWidth: true, margin: 'normal', sx: { position: 'absolute', opacity: 0, width: 0, height: 0 }, onKeyDown: (e) => e.preventDefault() } }}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Priority */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.5, borderBottom: '1px solid #f5f5f5' }}>
                            <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', color: '#aaa' }}>
                                <Iconify icon="mdi:flag-outline" width={15} />
                            </Box>
                            <Box sx={{ flex: 1 }}>
                                <Typography sx={{ fontSize: '11px', color: '#999', mb: 0.25 }}>Priority</Typography>
                                <Select
                                    size="small" variant="standard"
                                    value={priorityFilter} onChange={handlePriorityFilterChange}
                                    renderValue={(val) => {
                                        const cfg = PRIORITY_CONFIG[val];
                                        if (!cfg) return <Typography sx={{ fontSize: '13px', color: '#ccc' }}>Not set</Typography>;
                                        return (
                                            <Box sx={{
                                                display: 'inline-flex', fontSize: '12px', fontWeight: 500,
                                                color: cfg.color, bgcolor: cfg.bg,
                                                px: 1.25, py: 0.375, borderRadius: '6px',
                                            }}>
                                                {cfg.label}
                                            </Box>
                                        );
                                    }}
                                    sx={{
                                        '&:before': { borderBottom: 'none' },
                                        '&:after': { borderBottom: `2px solid ${ACCENT}` },
                                        '&:hover:not(.Mui-disabled):before': { borderBottom: 'none' },
                                        '.MuiSelect-select': { py: 0, pr: '20px !important' },
                                    }}
                                >
                                    {reminderPriority?.map((ele, index) => (
                                        <MenuItem key={index} value={ele?.value}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                <Iconify icon={getIconAndColor(ele?.value)?.icon || 'mdi:flag-outline'} width={14} sx={{ color: getIconAndColor(ele?.value)?.iconColor }} />
                                                {ele?.label}
                                            </Box>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </Box>
                        </Box>

                        {/* Assignee & Reporter - side by side */}
                        <Box sx={{ display: 'flex', borderBottom: '1px solid #f5f5f5' }}>
                            {/* Assignee */}
                            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, py: 1.5 }}>
                                <Box sx={{ width: 32, display: 'flex', justifyContent: 'center', color: '#aaa' }}>
                                    <Iconify icon="mdi:account-outline" width={15} />
                                </Box>
                                <Box>
                                    <Typography sx={{ fontSize: '11px', color: '#999', mb: 0.5 }}>Assignee</Typography>
                                    <Box sx={{ cursor: 'pointer' }}>
                                        {assigneeData?.length > 0 ?
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                <Tooltip title={getAssignees(data)} placement="bottom" TransitionComponent={Zoom}>
                                                    <Box sx={{ display: 'flex', gap: 0.25 }}>
                                                        {assigneeData?.slice(0, 2)?.map((assignee, index) => (
                                                            <AssignUserAvatar
                                                                key={index}
                                                                sx={{ ...assignUserAvatarStyle, width: 28, height: 28, border: 'none', '&:nth-child(2)': { ml: '-4px' } }}
                                                                src={assignee.user?.avatar || ''}
                                                                name={`${assignee.user?.first_name || ''} ${assignee.user?.last_name || ''}`}
                                                                role={assignee.role}
                                                                onClick={(e) => assignTaskHandler(e)}
                                                            />
                                                        ))}
                                                        {assigneeData?.length > 2 && (
                                                            <Box sx={{ ...assignLengthStyle, width: 28, height: 28, fontSize: '10px', border: 'none', ml: '-4px' }} onClick={(e) => assignTaskHandler(e)}>
                                                                +{assigneeData?.length - 2}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                </Tooltip>
                                            </Box> :
                                            <Box onClick={(e) => e.stopPropagation()}>
                                                <Box sx={{ ...assignTaskHandlerStyle, width: 28, height: 28, bgcolor: grey[200] }} onClick={(e) => assignTaskHandler(e)}>
                                                    <Iconify sx={{ color: 'common.grey' }} width={14} icon="eva:plus-fill" />
                                                </Box>
                                            </Box>
                                        }
                                    </Box>
                                </Box>
                            </Box>

                            {/* Reporter */}
                            {data?.created_by && !data.is_system_generated && (
                                <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1.25, py: 1.5 }}>
                                    <Box>
                                        <Typography sx={{ fontSize: '11px', color: '#999', mb: 0.5 }}>Reporter</Typography>
                                        <Tooltip title={`${capitalizeFirstLetter(data?.created_by?.first_name || '')} ${capitalizeFirstLetter(data?.created_by?.last_name || '')}`} placement="bottom">
                                            <CustomAvatar sx={{ height: 28, width: 28 }} src={data?.created_by?.avatar} name={data?.created_by?.first_name || ''} />
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )}
                        </Box>
                    </Collapse>
                    </Box>

                    {/* ── REMINDER NOTES ── */}
                    <Box sx={{ p: '16px 20px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Iconify icon="mdi:file-document-outline" width={14} sx={{ color: '#aaa' }} />
                            <Typography sx={{ fontSize: '12px', fontWeight: 500, color: '#888' }}>Reminder Notes</Typography>
                        </Box>
                        <TextField
                            sx={{
                                width: '100%',
                                '.MuiOutlinedInput-root': {
                                    bgcolor: '#fafafa', borderRadius: '8px', fontSize: '13px',
                                    '& fieldset': { borderColor: '#e8e8e8' },
                                    '&:hover fieldset': { borderColor: '#ccc' },
                                    '&.Mui-focused fieldset': { borderColor: ACCENT },
                                    '&.Mui-focused': { bgcolor: '#fff' },
                                },
                            }}
                            multiline maxRows={4} placeholder="Add notes for this task..."
                            value={detailValue || ''}
                            onChange={(e) => setDetailValue(e.target.value)}
                            onBlur={(e) => onBlurEvent(e.target.value)}
                            inputProps={{ maxLength: 2000 }}
                        />
                    </Box>

                    {/* ── COMMENTS ── */}
                    <Box sx={{ px: 2.5, pb: 2, borderTop: '1px solid #f0f0f0' }}>
                        {/* Comment input */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1.75 }}>
                            <Avatar sx={{ width: 30, height: 30, flexShrink: 0 }} src={user?.avatar || ''} />
                            <Box sx={{
                                flex: 1, display: 'flex', alignItems: 'center',
                                bgcolor: '#f5f5f5', borderRadius: '20px', pl: 1.75, pr: 0.5,
                            }}>
                                <TextField
                                    maxRows={2} multiline placeholder="Leave a comment..."
                                    value={comment || ''} onChange={(e) => setComment(e.target.value)}
                                    inputProps={{ maxLength: 1000 }}
                                    sx={{
                                        flex: 1,
                                        '.MuiOutlinedInput-notchedOutline': { display: 'none' },
                                        '.MuiInputBase-input': { p: '10px 0', fontSize: '13px' },
                                        '.MuiInputBase-root': { p: 0 },
                                    }}
                                />
                                <Box
                                    onClick={comment?.trim() ? () => onBlurCommentEvent() : undefined}
                                    sx={{
                                        width: 30, height: 30, borderRadius: '50%', cursor: comment?.trim() ? 'pointer' : 'default',
                                        bgcolor: comment?.trim() ? ACCENT : 'transparent',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <Iconify icon="mdi:send" width={16} sx={{ color: comment?.trim() ? '#fff' : '#ccc' }} />
                                </Box>
                            </Box>
                        </Box>

                        {/* Comment list toggle */}
                        <Box
                            onClick={() => setCommentHide(!commentHide)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.75, cursor: 'pointer', mb: commentHide ? 0 : 1.5 }}
                        >
                            <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#444' }}>
                                Comments ({leadNotes?.length || 0})
                            </Typography>
                            <Iconify
                                icon="mdi:chevron-down" width={14}
                                sx={{ color: '#888', transform: commentHide ? 'rotate(0deg)' : 'rotate(180deg)', transition: 'transform 0.2s ease' }}
                            />
                        </Box>

                        <Collapse in={!commentHide}>
                            {leadNotes && leadNotes?.length > 0 && leadNotes?.map((noteItem, index) => {
                                const aiNoteData = parseAINote(noteItem?.additional_notes);
                                return (
                                    <Box key={index} sx={{ display: 'flex', gap: 1.25, py: 1.25 }}>
                                        <Avatar sx={{ width: 28, height: 28, flexShrink: 0 }} src={noteItem?.created_by?.avatar || ''} />
                                        <Box>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#1a1a1a' }}>
                                                    {noteItem?.created_by?.first_name || ''} {noteItem?.created_by?.last_name || ''}
                                                </Typography>
                                                <Typography sx={{ fontSize: '11px', color: '#aaa' }}>
                                                    {moment(noteItem?.created_at, 'DD/MM/YYYY HH:mm:ss').format('DD MMM, YYYY')}
                                                </Typography>
                                            </Box>
                                            {aiNoteData ? (
                                                <Button
                                                    variant="outlined" size="small"
                                                    startIcon={<Iconify icon="mdi:robot-outline" />}
                                                    onClick={() => handleOpenAISummary(aiNoteData)}
                                                    sx={{
                                                        borderColor: '#7635dc', color: '#7635dc',
                                                        bgcolor: 'rgba(118, 53, 220, 0.08)',
                                                        '&:hover': { borderColor: '#5119b7', bgcolor: 'rgba(118, 53, 220, 0.16)' },
                                                        textTransform: 'none', fontWeight: 600, fontSize: '11px', mt: 0.5,
                                                    }}
                                                >
                                                    Show AI Call Summary
                                                </Button>
                                            ) : (
                                                <Box sx={{
                                                    fontSize: '13px', color: '#444', mt: 0.5,
                                                    bgcolor: '#f8f8f8', px: 1.25, py: 0.75,
                                                    borderRadius: '6px', display: 'inline-block',
                                                }}>
                                                    {noteItem?.additional_notes || noteItem?.note || noteItem?.lead_contact?.note || ''}
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Collapse>
                    </Box>
                </Box>
            </Box>

            {/* ── POPPERS & MODALS ── */}

            {openAssignTask && (
                <ClickAwayListener onClickAway={onClickAwayEvent}>
                    <Popper open={openAssignTask} anchorEl={anchorElAssign} transition placement="left" sx={{ zIndex: '999999' }}>
                        {({ TransitionProps }) => (
                            <Grow {...TransitionProps}>
                                <Paper sx={{ borderRadius: 2 }}>
                                    <AssignTaskPopup open={openAssignTask} onClose={() => allPopupCloseHandler('Assigned')} leadDetail={data} reminder={data} assignees={assigneeData?.map((ele) => ele.id)} />
                                </Paper>
                            </Grow>
                        )}
                    </Popper>
                </ClickAwayListener>
            )}
            {aiSummaryModalOpen && selectedAIData && (
                <AISummaryModal
                    open={aiSummaryModalOpen} onClose={handleCloseAISummary} onCloseAll={handleCloseAISummary} onBackToScript={handleCloseAISummary}
                    data={{
                        classification: selectedAIData.classification?.status || 'Unknown', classificationEmoji: selectedAIData.classification?.emoji || '',
                        readinessScore: selectedAIData.classification?.readinessScore || 0, budget: selectedAIData.classification?.budget || '',
                        availability: selectedAIData.classification?.availability || '', intent: selectedAIData.classification?.intent || '',
                        summary: selectedAIData.summary?.aiSummaryText || '', keyQuote: selectedAIData.summary?.keyQuote || '',
                        sentiment: selectedAIData.summary?.sentiment || 'Neutral', highlights: selectedAIData.summary?.highlights || [],
                        concerns: selectedAIData.concerns || [], tags: selectedAIData.tags || [],
                        bookAppointment: selectedAIData.recommendedActions?.bookAppointment || { recommended: false },
                        sendWhatsApp: selectedAIData.recommendedActions?.sendWhatsApp || { recommended: false },
                        sendEmail: selectedAIData.recommendedActions?.sendEmail || { recommended: false },
                        addReminder: selectedAIData.recommendedActions?.addReminder || { recommended: false },
                        nextSteps: selectedAIData.recommendedActions?.nextSteps || [],
                    }}
                    leadName={selectedAIData.leadInfo?.name || ''} leadInitials={selectedAIData.leadInfo?.initials || ''}
                    callType={selectedAIData.callInfo?.callType || 'Outbound'} callStartTime={selectedAIData.callInfo?.callStartTime || ''}
                    callDuration={selectedAIData.callInfo?.callDuration || ''} dataCompleteness={selectedAIData.callInfo?.dataCompleteness || 0}
                    viewMode savedAIData={selectedAIData}
                />
            )}
        </FormProvider>
    );
};

TaskSidebar.propTypes = {
    data: PropTypes.any, type: PropTypes.string,
    overViewbtns: PropTypes.func, getReminderBtn: PropTypes.func, handleFollowUp: PropTypes.func,
    handleSendWhatsApp: PropTypes.func,
    handleAttendClick: PropTypes.func, handleAttendFailedClick: PropTypes.func,
    setReloadCounters: PropTypes.func, onClose: PropTypes.func,
    parentType: PropTypes.string, onTaskUpdate: PropTypes.func,
};

export default TaskSidebar;
